import { evaluateExpression } from '../utils/evaluateExpression.js';
import { castArgumentDataType } from './castArgumentDataType.js';
import { serializeLexical } from './getArgumentValue/serializeLexical.js';
import { buildArgResult } from './getArgumentValue/buildArgResult.js';

export const getArgumentValue = async ({ arg = {}, def = {}, responses, opts = {}, globalVariables, timezone, resolvedLexical }) => {
  if (typeof arg !== 'object') {
    if (def?.trace === true || opts?.returnIndices || opts?.returnErrors) {
      return buildArgResult({ value: arg, indices: undefined, errors: [], originalValue: undefined }, { trace: def?.trace === true, returnIndices: opts?.returnIndices, returnErrors: opts?.returnErrors, returnOriginalValue: opts?.returnOriginalValue });
    }
    return arg;
  }

  const errors = [];
  const dataType = def?.dataType || arg?.dataType;
  const mapper = def?.mapper;
  const trace = def?.trace === true;
  const escapeArguments = def?.escapeArguments;
  const returnIndices = opts?.returnIndices;
  const returnErrors = opts?.returnErrors;
  const returnOriginalValue = opts?.returnOriginalValue;
  const hasMultipleValues = def?.multipleValues?.isActive;
  const multipleValueDelimiter = def?.multipleValues?.delimiter || ',';
  const multipleValueFormat = def?.multipleValues?.format || 'string';
  const fileMode = def?.fileMode || 'file';
  const fileMaxSize = def?.fileMaxSize;
  const component = def?.component;
  const defaultValue = def?.defaultValue;
  let itemDataType = def?.itemDataType || arg?.itemDataType || (dataType === 'array' && !def.dataType ? 'object' : undefined);

  const hasExplicitArgumentValue = arg?.value !== undefined && arg?.value !== null;
  let runningValue = hasExplicitArgumentValue ? arg.value : def?.defaultValue;

  const computedTimezone = arg?.timezone || timezone || 'EDT';

  if (!['array', 'list'].includes(dataType) && hasMultipleValues) {
    itemDataType = dataType;
  }

  if (escapeArguments?.enabled) {
    opts.escapeArguments = escapeArguments;
  }

  if (Array.isArray(arg)) {
    const mappedValues = [];
    let isCollectionReference = false;

    for (let i = 0; i < arg.length; i++) {
      const singleArg = arg[i];
      const canResolveCollection = def?.dataType === 'array' && ['string', 'number', 'boolean'].includes(itemDataType) && arg.length === 1 && singleArg?.type === 'lexical' && singleArg?.value?.root && !mapper && !hasMultipleValues;
      const childLexical = canResolveCollection ? serializeLexical(singleArg.value, responses, { ...opts, captureSingleReferenceArray: true }) : undefined;
      isCollectionReference = Array.isArray(childLexical?.singleReferenceArray);

      const childDef = { ...def, dataType: isCollectionReference ? 'array' : itemDataType, itemDataType: isCollectionReference ? itemDataType : undefined };
      if (isCollectionReference) {
        // An explicit collection, including [], must not activate scalar defaults.
        childDef.defaultValue = undefined;
      }

      const result = await getArgumentValue({ arg: singleArg, def: childDef, responses, opts: { ...opts, returnIndices: true, returnErrors: true, returnOriginalValue: true }, globalVariables, resolvedLexical: childLexical });
      mappedValues.push(result);
    }

    runningValue = hasMultipleValues && multipleValueFormat === 'string' ? mappedValues.map((n) => n?.value).join(multipleValueDelimiter) : mappedValues.map((n) => n?.value);
    if (isCollectionReference) {
      runningValue = mappedValues[0].value;
    }

    if (!trace && !returnIndices && !returnErrors) {
      return runningValue;
    }

    return buildArgResult(
      {
        value: runningValue,
        indices: mappedValues.map((n) => n?.indices).flat(),
        errors: mappedValues.map((n) => n?.errors).flat(),
        originalValue: mappedValues.map((n) => n?.originalValue).flat()
      },
      { trace, returnIndices, returnErrors, returnOriginalValue }
    );
  }

  let indices;
  let originalValue;
  if (arg?.type === 'lexical') {
    const result = resolvedLexical || serializeLexical(runningValue, responses, opts);
    runningValue = result.singleReferenceArray || result.value;
    originalValue = result.value;
    indices = result.indices;
  }

  try {
    runningValue = castArgumentDataType({ value: runningValue, dataType, itemDataType, timezone: computedTimezone, fileMode, fileMaxSize, component });
  } catch (error) {
    console.log(`Could not convert ${def?.key || 'argument'} into ${dataType}: ${error.message}`, { value: runningValue, type: typeof runningValue, desiredType: dataType });
    errors.push({ code: 'INVALID_ARGUMENT', message: `Could not convert ${def?.key || 'argument'} into ${dataType}: ${error.message}`, value: runningValue, severity: 'error' });
  }

  if (mapper) {
    const isArrowFunction = !mapper.includes('return');
    const evalResult = await evaluateExpression({
      expression: mapper,
      input: runningValue,
      globalVariables,
      arrowFunction: isArrowFunction,
      inputLabel: ['input', 'data'],
      useVM: false,
      meta: { source: 'getArgumentValue' }
    });

    if (evalResult?.success) {
      runningValue = evalResult?.data;
    }
  }

  if (dataType === 'array' && !!defaultValue && Array.isArray(runningValue) && runningValue?.filter(Boolean).length === 0) {
    runningValue = castArgumentDataType({ value: defaultValue, dataType, itemDataType, timezone: computedTimezone, fileMode, fileMaxSize, component });
  }

  if (!trace && !returnIndices && !returnErrors) {
    return runningValue;
  }

  return buildArgResult({ value: runningValue, indices, errors, originalValue }, { trace, returnIndices, returnErrors, returnOriginalValue });
};
